import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert } from '@/components/ui/alert'
import { getJSON, patchJSON } from '@/lib/api'

type Setting = 'hidden' | 'optional' | 'required'
const labels: Record<Setting, string> = {
  hidden: 'Hidden',
  optional: 'Optional',
  required: 'Required',
}

export function JoinFormSettingsPage() {
  const client = useQueryClient()
  const query = useQuery<{ join_form_settings: { join_chapter_setting: Setting | null } }>({
    queryKey: ['join-form-settings'],
    queryFn: () => getJSON('/api/v1/join_form_settings'),
    retry: false,
  })
  const [value, setValue] = React.useState<Setting>('optional')
  React.useEffect(() => {
    if (query.data) setValue(query.data.join_form_settings.join_chapter_setting ?? 'optional')
  }, [query.data])
  const save = useMutation({
    mutationFn: () =>
      patchJSON('/api/v1/join_form_settings', {
        join_form_settings: { join_chapter_setting: value },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['join-form-settings'] }),
  })
  if (query.isLoading)
    return <p className="text-body-sm text-muted-foreground">Loading join form settings...</p>
  if (query.isError) return <Alert>Join form settings unavailable</Alert>
  return (
    <div className="max-w-xl space-y-4">
      <header>
        <h1 className="text-heading-sm font-semibold text-foreground">Join form settings</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Choose whether applicants may select a Chapter.
        </p>
      </header>
      <div className="space-y-1.5">
        <Label htmlFor="join-chapter-setting">Chapter selection</Label>
        <Select value={value} onValueChange={(next) => setValue(next as Setting)}>
          <SelectTrigger id="join-chapter-setting">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['hidden', 'optional', 'required'] as Setting[]).map((setting) => (
              <SelectItem key={setting} value={setting}>
                {labels[setting]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? 'Saving...' : 'Save'}
      </Button>
    </div>
  )
}
